"use client";

import * as React from "react";
import { Check, ChevronDown } from "lucide-react";

import { cn } from "../utils";
import { Badge } from "../data-display/badge";
import { Button } from "../inputs/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../primitives/command";
import { Checkbox } from "../primitives/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../primitives/popover";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface FilterOption {
  value: string;
  label: string;
  /** Optional icon rendered before the label */
  icon?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// ComboboxFilter — single-select combobox for toolbar filtering
// ---------------------------------------------------------------------------

export interface ComboboxFilterProps {
  /** The label shown on the trigger (e.g. "Status") */
  label: string;
  options: FilterOption[];
  value: string | undefined;
  onValueChange: (value: string | undefined) => void;
  /** Placeholder shown in the search field */
  searchPlaceholder?: string;
  className?: string;
}

export function ComboboxFilter({
  label,
  options,
  value,
  onValueChange,
  searchPlaceholder = "Search…",
  className,
}: ComboboxFilterProps) {
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      const id = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          className={cn("h-9 gap-1.5 text-sm font-normal", className)}
        >
          {label}
          {selected && (
            <>
              <span className="text-muted-foreground">:</span>
              <span className="font-medium">{selected.label}</span>
            </>
          )}
          <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandInput ref={inputRef} placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    onValueChange(option.value === value ? undefined : option.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-3.5 w-3.5 shrink-0",
                      value === option.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {option.icon && <span className="mr-2 flex shrink-0">{option.icon}</span>}
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// ToggleFilter — on/off toolbar filter for a single boolean dimension
// ---------------------------------------------------------------------------

export interface ToggleFilterProps {
  /** The label shown on the trigger (e.g. "Subtasks") */
  label: string;
  pressed: boolean;
  onPressedChange: (pressed: boolean) => void;
  /** Optional icon rendered before the label */
  icon?: React.ReactNode;
  className?: string;
}

export function ToggleFilter({
  label,
  pressed,
  onPressedChange,
  icon,
  className,
}: ToggleFilterProps) {
  return (
    <Button
      type="button"
      variant="outline"
      aria-pressed={pressed}
      onClick={() => onPressedChange(!pressed)}
      className={cn(
        "h-9 gap-1.5 text-sm font-normal",
        pressed && "border-primary/40 bg-accent text-accent-foreground",
        className,
      )}
    >
      {icon && <span className="flex shrink-0">{icon}</span>}
      {label}
    </Button>
  );
}

/**
 * A labelled group of filter controls, for a filter popover that holds more
 * than one kind of facet. Typography matches the "Display properties" heading
 * in `DataTableViewControls` so a grouped popover and an ungrouped one read
 * as the same control.
 */
export function FilterGroup({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  /** Secondary text after the label — a unit, a legend. */
  hint?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-1.5 border-t pt-2 first:border-t-0 first:pt-0", className)}>
      <p className="text-xs font-medium text-muted-foreground">
        {label}
        {hint ? <span className="ml-1 font-normal">{hint}</span> : null}
      </p>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ComboboxMultiFilter — multi-select combobox for toolbar filtering
// ---------------------------------------------------------------------------

export interface ComboboxMultiFilterProps {
  /** The label shown on the trigger (e.g. "Status") */
  label: string;
  options: FilterOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  /** Placeholder shown in the search field */
  searchPlaceholder?: string;
  className?: string;
}

export function ComboboxMultiFilter({
  label,
  options,
  value,
  onValueChange,
  searchPlaceholder = "Search…",
  className,
}: ComboboxMultiFilterProps) {
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      const id = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [open]);

  const toggle = (optionValue: string) => {
    const next = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onValueChange(next);
  };

  const count = value.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          className={cn("h-9 gap-1.5 text-sm font-normal", className)}
        >
          {label}
          {count > 0 && (
            <Badge
              variant="secondary"
              className="ml-0.5 h-5 rounded-md px-1.5 text-[10px] font-semibold"
            >
              {count}
            </Badge>
          )}
          <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandInput ref={inputRef} placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const checked = value.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => toggle(option.value)}
                  >
                    <Checkbox
                      checked={checked}
                      className="pointer-events-none mr-2"
                      tabIndex={-1}
                    />
                    {option.icon && <span className="mr-2 flex shrink-0">{option.icon}</span>}
                    {option.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {count > 0 && (
              <div className="border-t border-border p-1">
                <CommandItem
                  onSelect={() => onValueChange([])}
                  className="justify-center text-xs text-muted-foreground"
                >
                  Clear filters
                </CommandItem>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
